// webapp/src/lib/invokeAuthed.ts
//
// Authenticated wrapper around `supabase.functions.invoke`.
//
// WHY THIS EXISTS (root-caused 2026-05-22):
//   supabase-js (v2.x) does NOT keep the Functions client's auth header in sync
//   with the session the way the PostgREST client is. Concretely:
//     1. `SupabaseClient._handleTokenChanged` only calls `realtime.setAuth` on
//        SIGNED_IN / TOKEN_REFRESHED — never the Functions client.
//     2. The internal auth fetch-wrapper injects the live user token ONLY when
//        the request has no `Authorization` header yet, and otherwise falls back
//        to the anon key (`token = await getAccessToken() ?? anonKey`).
//     3. `get functions()` rebuilds the FunctionsClient with the shared global
//        headers, so `invoke` can forward a stale / anon `Authorization`.
//   Net effect: `functions.invoke` intermittently sends an anon/stale token, and
//   Edge Functions that verify the user JWT return 401. Observed in prod across
//   notify-pack-booking, cancel-booking-user, reserve-credit, send-push. The
//   `.rpc()` / `.from()` (PostgREST) path is unaffected (`auth.uid()` works).
//
// THE FIX: read the current session and pass an EXPLICIT `Authorization` header.
// The fetch-wrapper respects an Authorization that is already present, and the
// per-call `headers` win the `Object.assign` merge inside `FunctionsClient.invoke`
// — so this deterministically sends the real user token.
//
// `getSession()` returns the session from the auth module's in-memory state,
// which `autoRefreshToken` keeps current in the background — so it is the right
// source for the freshest token without forcing a network round-trip.

/**
 * Minimal structural client surface — lets unit tests inject a stub without
 * importing the real supabase-js client (which throws on missing env vars).
 */
export type AuthedInvokeClient = {
  auth: {
    getSession: () => Promise<{
      data: { session: { access_token?: string | null } | null }
    }>
  }
  functions: {
    invoke: (
      name: string,
      options: { body?: Record<string, unknown>; headers?: Record<string, string> },
    ) => Promise<{ data?: unknown; error?: unknown }>
  }
}

/**
 * Invoke a Supabase Edge Function with the current user's access token explicitly
 * attached as the `Authorization` header. Use this for EVERY browser → Edge
 * Function call that requires a logged-in user; do not call
 * `supabase.functions.invoke` directly for authed functions (see file header).
 *
 * When there is no session (anonymous flow), no `Authorization` is added — the
 * call proceeds as anonymous, which is correct for anon-tolerant functions.
 */
export async function invokeAuthed(
  client: AuthedInvokeClient,
  fn: string,
  options: { body?: Record<string, unknown>; headers?: Record<string, string> } = {},
): Promise<{ data?: unknown; error?: unknown }> {
  const { data: { session } } = await client.auth.getSession()
  const headers: Record<string, string> = { ...(options.headers ?? {}) }
  if (session?.access_token) {
    headers.Authorization = `Bearer ${session.access_token}`
  }
  return client.functions.invoke(fn, { ...options, headers })
}
